import { createRootRoute, Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import { useEffect } from 'react';
import { NavRail } from '@/components/nav-rail';
import { TopBar } from '@/components/top-bar';
import { ThemeProvider } from '@/components/theme-provider';
import { BrandingProvider } from '@/contexts/branding-context';
import { TenantBrandingProvider } from '@/components/branding';
import { ImpersonationProvider } from '@/contexts/impersonation-context';
import { AuthProvider } from '@/contexts/auth-context';
import { ImpersonationBanner } from '@/components/impersonation/impersonation-banner';
import { Toaster } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { ErrorBoundary } from '@/components/error-boundary';
import { GlobalSelectionToolbar } from '@/components/shared/global-selection-toolbar';
import { AttachedHighlightsProvider } from '@/contexts/attached-highlights-context';
import { usePendingApprovals } from '@/lib/use-agent-approvals';
import { BugReportButton } from '@/components/bug-report/bug-report-button';
import { useAuth } from '@/contexts/auth-context';
import { recordRoute } from '@/lib/route-history-tracker';
import { useWebVitals } from '@/lib/use-web-vitals';
import { reportWebVitals } from '@/lib/report-web-vitals';

export const Route = createRootRoute({
  component: RootLayout,
});

// Public routes that don't require authentication.
// `/invite` covers `/invite/$token` (W1a-06 invitation accept page).
// `/setup` covers `/setup/$token` (W1a-19 install bootstrap page).
// `/shared` covers `/shared/$token` (#1081 conversation share snapshot).
// The shared route must render WITHOUT the authenticated app shell so
// that an external recipient (no account, no token) can view the
// snapshot — without this entry, the protected-route guard 302s the
// recipient to /login, which is exactly the bug #1081 reported.
const publicRoutes = ['/login', '/auth/callback', '/invite', '/setup', '/shared'];

function RootLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const isPublicRoute = publicRoutes.some((route) =>
    location.pathname.startsWith(route)
  );

  // Issue #87 — when the user lands on a protected route without a
  // token, we MUST NOT render the authenticated shell while the
  // redirect navigation is in flight. Otherwise the previous render
  // (e.g. after Sign Out, before TanStack Router finishes the route
  // transition) flashes protected UI for one frame. We compute the
  // token gate synchronously here and short-circuit the render below.
  const hasAccessToken =
    typeof window !== 'undefined' && !!localStorage.getItem('accessToken');

  // Check authentication for protected routes
  useEffect(() => {
    if (!isPublicRoute && !hasAccessToken) {
      // `replace: true` keeps the protected URL out of history so the
      // browser back-button cannot return to it post-sign-out.
      navigate({ to: '/login', replace: true });
    }
  }, [isPublicRoute, navigate, location.pathname, hasAccessToken]);

  // Track recently-visited routes for the bug-report metadata blob.
  // Dedups consecutive duplicates inside `recordRoute` so re-renders /
  // fast-refresh don't pollute the ring.
  useEffect(() => {
    recordRoute(location.pathname);
  }, [location.pathname]);

  // Core Web Vitals collection — wired at the root so every page view
  // contributes to the aggregate. Reports best-effort via sendBeacon
  // (survives page unload). No impact on UX — fires after interaction.
  useWebVitals(reportWebVitals);

  // For public routes, render without the app layout
  if (isPublicRoute) {
    return (
      <BrandingProvider>
        <ThemeProvider>
          <TenantBrandingProvider>
            <div className="min-h-screen bg-background">
              <Outlet />
              {import.meta.env.DEV && <TanStackRouterDevtools />}
            </div>
          </TenantBrandingProvider>
        </ThemeProvider>
      </BrandingProvider>
    );
  }

  // Issue #87 — synchronous render gate. When there's no access token
  // and we're on a protected route, render an empty shell instead of
  // the authenticated app while the useEffect-driven navigate({/login})
  // is in flight. Without this gate the rail/topbar/main outlet would
  // mount for one frame post-sign-out, displaying the previous user's
  // protected content before the redirect lands.
  if (!hasAccessToken) {
    return (
      <BrandingProvider>
        <ThemeProvider>
          <TenantBrandingProvider>
            <div className="min-h-screen bg-background" />
          </TenantBrandingProvider>
        </ThemeProvider>
      </BrandingProvider>
    );
  }

  // For authenticated routes: top bar + icon rail + content area
  return (
    <BrandingProvider>
      <ThemeProvider>
        <TenantBrandingProvider>
        <AuthProvider>
        <ImpersonationProvider>
          <AttachedHighlightsProvider>
          <TooltipProvider delayDuration={200}>
          <ImpersonationBanner />
          <Toaster richColors position="top-right" />

          <div className="flex flex-col h-screen overflow-hidden bg-background">
            {/*
              Skip-to-content link — WCAG 2.4.1 (Bypass Blocks). Visually
              hidden until focused via keyboard so it doesn't disturb the
              visual design, but the first Tab from the top of any page
              surfaces it, letting keyboard / screen-reader users skip
              past the top bar + nav rail in one keystroke.
            */}
            <a
              href="#main-content"
              className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-primary-foreground focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            >
              Skip to main content
            </a>

            {/*
              Top bar lives inside a <header role="banner"> landmark so
              screen-reader users (and axe-core `region` rule) can find
              and jump to the global page chrome. Resolves the "region"
              cluster (22 nodes, 11/11 rails) flagged by the deep a11y
              audit.
            */}
            <header role="banner">
              <TopBar />
            </header>

            {/* Below top bar: icon rail + content side by side */}
            <div className="flex flex-1 overflow-hidden">
              {/* Icon Rail — fixed width left column. The NavRail component
                  already emits its own <nav> landmark internally. */}
              <NavRailWithBadges />

              {/* Content Area — fills remaining space. `id="main-content"`
                  is the target of the skip link above. */}
              <main id="main-content" className="flex-1 flex flex-col overflow-hidden">
                <ErrorBoundary>
                  <Outlet />
                </ErrorBoundary>
              </main>
            </div>
          </div>

          {/* Global selection toolbar — highlight any text anywhere to get Ask/Explain/Copy */}
          <GlobalSelectionToolbar />

          {/* Floating bug-report "+" button, bottom-left. Self-hides
              on /login, /auth/callback, /invite, /setup and when the
              user is not authenticated. */}
          <BugReportButton />

          {import.meta.env.DEV && <TanStackRouterDevtools />}
          </TooltipProvider>
          </AttachedHighlightsProvider>
        </ImpersonationProvider>
        </AuthProvider>
        </TenantBrandingProvider>
      </ThemeProvider>
    </BrandingProvider>
  );
}

// Real-data badge wrapper — pulls pending approvals count from backend.
// Approvals are a tenant-scoped concept; in sysadmin context the
// `/agent-approvals?status=pending` endpoint 400s with NO_TENANT_CONTEXT.
// Three audit reports (R5 Models #486, R6 Audit #487, R6 Administrators
// #488) flagged the resulting console error spam on every sysadmin
// route. PR #490 first added a `!isSysadmin` gate; PR #546's Sysadmin
// Health E2E audit then caught that the fetch STILL fired 20× across
// surfaces. Root cause: `AuthProvider` starts with `contextKind='tenant'`
// (so `isSysadmin === false`) and only flips to `'sysadmin'` after the
// async JWT decode in `fetchUser()` resolves. During that initial mount
// window — exactly one render — the gate is open, the query fires, the
// backend returns 400 NO_TENANT_CONTEXT, and the console gets red spam.
//
// Fix: also gate on `!isLoading` so we wait until auth is resolved
// before deciding. When `isLoading === true` we render the badge with
// `approvals=0` and no fetch — same as the sysadmin steady state. Once
// the JWT is decoded, the right branch takes over.
function NavRailWithBadges() {
  const { isSysadmin, isLoading } = useAuth();
  // Suppress the fetch in two situations:
  //   1. We already know we're sysadmin → tenant endpoint will 400.
  //   2. We don't yet know who we are (initial mount, async fetchUser
  //      in flight) → firing now would 400 on every sysadmin hard-nav.
  const enabled = !isLoading && !isSysadmin;
  return <NavRailWithApprovalBadge enabled={enabled} />;
}

function NavRailWithApprovalBadge({ enabled }: { enabled: boolean }) {
  // When `enabled === false` we skip the fetch entirely (rather than
  // suppress the 400 after the fact) so we don't pollute audit/console
  // logs in sysadmin context.
  if (!enabled) {
    return <NavRail badges={{ approvals: 0 }} />;
  }
  return <NavRailWithApprovalBadgeFetching />;
}

function NavRailWithApprovalBadgeFetching() {
  const { data: pending = [] } = usePendingApprovals();
  return <NavRail badges={{ approvals: pending.length }} />;
}
